import emailjs from '@emailjs/browser';
emailjs.init('5LCZWAm1bEoxz1KzZ');
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import ReCAPTCHA from 'react-google-recaptcha';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const contactInfo = [
  { icon: Mail, label: 'Email', value: 'admin@aerwlifestyle.co.uk', href: 'mailto:admin@aerwlifestyle.co.uk' },
  { icon: Phone, label: 'Phone', value: '07803 174495', href: 'tel:07803174495' },
  { icon: MapPin, label: 'Service Area', value: 'East Midlands, West Midlands, East of England and London.', href: null },
  { icon: Clock, label: 'Availability', value: 'Mon-Sat, 8am-8pm', href: null },
];

const faqs = [
  {
    question: 'What is non-regulated companionship?',
    answer: 'Non-regulated companionship refers to social support services that don\'t require CQC registration. This includes friendly visits, conversation, accompanying on outings, shopping assistance, and general lifestyle support. We do not provide personal care, medical care, or assistance with washing, dressing, toileting, or medication.'
  },
  {
    question: 'How do I arrange a companion?',
    answer: 'Simply contact us via phone, email, or the enquiry form on this page. We\'ll arrange a free consultation to discuss your needs, match you with a suitable companion, and agree on a schedule that works for you.'
  },
  {
    question: 'What areas do you cover?',
    answer: 'AERW Lifestyle provides services across the United Kingdom. Service availability may vary by location, so please contact us to confirm coverage in your specific area.'
  },
  {
    question: 'How much do your services cost?',
    answer: 'Our pricing starts from £20 for a one-hour companionship visit. Extended visits, half-day support, and outings are also available. Pricing may vary depending on location and requirements. We provide transparent quotes after an initial consultation.'
  },
  {
    question: 'Are your companions DBS checked?',
    answer: 'Yes, all our companions undergo thorough background checks including DBS (Disclosure and Barring Service) checks. We take the safety and trust of our clients very seriously.'
  },
  {
    question: 'Can I choose my companion?',
    answer: 'We carefully match companions based on personality, interests, and preferences. During the consultation, we discuss what you\'re looking for, and we do our best to find the perfect match. If a match doesn\'t feel right, we\'re happy to suggest alternatives.'
  },
  {
    question: 'What\'s the difference between your services and care agencies?',
    answer: 'Unlike care agencies that provide regulated personal care (washing, dressing, medication), AERW Lifestyle focuses solely on companionship and lifestyle support. We provide social connection, friendly presence, accompanied outings, and everyday assistance that supports independence and wellbeing.'
  },
  {
    question: 'How do I cancel or reschedule a visit?',
    answer: 'We understand plans can change. Simply contact us as soon as possible to reschedule. We ask for reasonable notice for cancellations, but we\'re always flexible and understanding.'
  },
];

export const ContactPage = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    supportFor: '',
    supportType: '',
    contactMethod: '',
    message: ''
  });

  const handleSubmit = (e) => {
  e.preventDefault();

  // Tight validation
  if (!formData.fullName.trim()) return alert("Full name is required");
  if (!formData.phone.trim()) return alert("Phone number is required");
  if (!/^[0-9+ ]{7,15}$/.test(formData.phone)) return alert("Enter a valid phone number");
  if (!formData.email.trim()) return alert("Email is required");
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return alert("Enter a valid email");
  if (!formData.supportFor) return alert("Select who support is for");
  if (!captcha) {  alert("Please verify you are not a robot");  return;}

  const templateParams = {
  name: formData.fullName,
  phone: formData.phone,
  email: formData.email,
  support_for: formData.supportFor,
  support_type: formData.supportType || "Not specified",
  contact_method: formData.contactMethod || "Not specified",
  message: formData.message || "No message provided"
};

  emailjs.send(
  'service_8diavld',
  'template_a2p53ii',
  {
    name: formData.fullName,
    phone: formData.phone,
    email: formData.email,
    support_for: formData.supportFor,
    support_type: formData.supportType || "Not specified",
    contact_method: formData.contactMethod || "Not specified",
    message: formData.message || "No message provided",
    'g-recaptcha-response': captcha
  },
  '5LCZWAm1bEoxz1KzZ'
)
.then(() => {
  setFormSubmitted(true);
  setCaptcha(null);
})
  .catch((error) => {
  console.error("EmailJS ERROR:", error);
  alert("Failed to send. Check console.");
});
};

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 bg-white" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto">
            <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#3A7BD5] mb-4 block">
              Contact Us
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1E4F8A] leading-tight mb-6">
              Get in Touch
            </h1>
            <p className="text-lg text-[#4A5568] leading-relaxed">
              Ready to learn more about our companionship services? We'd love to hear from you. 
              Contact us for a free, no-obligation consultation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Strip */}
      <section className="py-8 bg-[#1E4F8A]" data-testid="contact-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-4"
              >
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-white/70">{item.label}</p>
                  {item.href ? (
                    <a href={item.href} className="text-white font-medium hover:text-white/80 transition-colors">
                      {item.value}
                    </a>
                  ) : (
                    <p className="text-white font-medium">{item.value}</p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 md:py-28 bg-[#F7F9FC]" data-testid="contact-form-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp}>
            <Card className="bg-white border-[#E2E8F0] shadow-lg">
              <CardContent className="p-8 md:p-12">
                {formSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                    data-testid="form-success"
                  >
                    <div className="w-20 h-20 bg-[#F7F9FC] rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 className="w-10 h-10 text-[#3A7BD5]" />
                    </div>
                    <h3 className="font-serif text-2xl text-[#1E4F8A] mb-4">Thank You for Your Enquiry</h3>
                    <p className="text-[#4A5568] mb-6">
                      We've received your message and will be in touch within 24 hours to discuss 
                      your companionship needs. We look forward to speaking with you.
                    </p>
                    <button
                      onClick={() => {
                        setFormSubmitted(false);
                        setFormData({
                          fullName: '',
                          phone: '',
                          email: '',
                          supportFor: '',
                          supportType: '',
                          contactMethod: '',
                          message: ''
                        });
                      }}
                      className="text-[#3A7BD5] font-medium hover:text-[#1E4F8A] transition-colors"
                    >
                      Submit another enquiry
                    </button>
                  </motion.div>
                ) : (
                  <>
                    <div className="text-center mb-10">
                      <h2 className="font-serif text-2xl md:text-3xl text-[#1E4F8A] mb-4">
                        Request a Consultation
                      </h2>
                      <p className="text-[#4A5568]">
                        Fill out the form below and we'll get back to you within 24 hours.
                      </p>
                    </div>
                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="fullName" className="text-[#1A1A1A]">Full Name *</Label>
                          <Input
                            id="fullName"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleChange}
                            placeholder="Your full name"
                            required
                            className="border-[#E2E8F0] focus:border-[#3A7BD5] focus:ring-[#3A7BD5]"
                            data-testid="input-fullname"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phone" className="text-[#1A1A1A]">Phone Number *</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="Your phone number"
                            required
                            className="border-[#E2E8F0] focus:border-[#3A7BD5] focus:ring-[#3A7BD5]"
                            data-testid="input-phone"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-[#1A1A1A]">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          required
                          className="border-[#E2E8F0] focus:border-[#3A7BD5] focus:ring-[#3A7BD5]"
                          data-testid="input-email"
                        />
                      </div>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label className="text-[#1A1A1A]">Who is the support for? *</Label>
                          <Select
                            value={formData.supportFor}
                            onValueChange={(value) => handleSelectChange('supportFor', value)}
                            required
                          >
                            <SelectTrigger className="border-[#E2E8F0]" data-testid="select-support-for">
                              <SelectValue placeholder="Select an option" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="myself"className="focus:bg-blue-50 focus:text-[#1E4F8A]">Myself</SelectItem>
                              <SelectItem value="parent"className="focus:bg-blue-50 focus:text-[#1E4F8A]">My parent</SelectItem>
                              <SelectItem value="spouse"className="focus:bg-blue-50 focus:text-[#1E4F8A]">My spouse/partner</SelectItem>
                              <SelectItem value="relative"className="focus:bg-blue-50 focus:text-[#1E4F8A]">Another relative</SelectItem>
                              <SelectItem value="friend"className="focus:bg-blue-50 focus:text-[#1E4F8A]">A friend</SelectItem>
                              <SelectItem value="client"className="focus:bg-blue-50 focus:text-[#1E4F8A]">A client (professional referral)</SelectItem>
                              <SelectItem value="other"className="focus:bg-blue-50 focus:text-[#1E4F8A]">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[#1A1A1A]">Type of support needed</Label>
                          <Select
                            value={formData.supportType}
                            onValueChange={(value) => handleSelectChange('supportType', value)}
                          >
                            <SelectTrigger className="border-[#E2E8F0]" data-testid="select-support-type">
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="companionship"className="focus:bg-blue-50 focus:text-[#1E4F8A]">Companionship visits</SelectItem>
                              <SelectItem value="outings"className="focus:bg-blue-50 focus:text-[#1E4F8A]">Outings & activities</SelectItem>
                              <SelectItem value="shopping"className="focus:bg-blue-50 focus:text-[#1E4F8A]">Shopping support</SelectItem>
                              <SelectItem value="appointments"className="focus:bg-blue-50 focus:text-[#1E4F8A]">Appointment accompaniment</SelectItem>
                              <SelectItem value="checkins"className="focus:bg-blue-50 focus:text-[#1E4F8A]">Regular check-ins</SelectItem>
                              <SelectItem value="multiple"className="focus:bg-blue-50 focus:text-[#1E4F8A]">Multiple services</SelectItem>
                              <SelectItem value="unsure"className="focus:bg-blue-50 focus:text-[#1E4F8A]">Not sure yet</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#1A1A1A]">Preferred contact method</Label>
                        <Select
                          value={formData.contactMethod}
                          onValueChange={(value) => handleSelectChange('contactMethod', value)}
                        >
                          <SelectTrigger className="border-[#E2E8F0]" data-testid="select-contact-method">
                            <SelectValue placeholder="Select preference" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="phone"className="focus:bg-blue-50 focus:text-[#1E4F8A]">Phone call</SelectItem>
                            <SelectItem value="email"className="focus:bg-blue-50 focus:text-[#1E4F8A]">Email</SelectItem>
                            <SelectItem value="whatsapp"className="focus:bg-blue-50 focus:text-[#1E4F8A]">WhatsApp</SelectItem>
                            <SelectItem value="any"className="focus:bg-blue-50 focus:text-[#1E4F8A]">No preference</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="message" className="text-[#1A1A1A]">Your Message</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us a bit about what you're looking for..."
                          rows={5}
                          className="border-[#E2E8F0] focus:border-[#3A7BD5] focus:ring-[#3A7BD5] resize-none"
                          data-testid="input-message"
                        />
                      </div>
                      <div className="flex items-start gap-2 text-sm">
                              <input type="checkbox" required />
                                <span>
                                   I agree to the <a href="/policies">Terms & Conditions</a> and <a href="/policies">Privacy Policy</a>
                                 </span>
                             </div>
                      <ReCAPTCHA
                          sitekey="6LfFJp0sAAAAAKyE4AGHjf96t3VEJv2UKbnHAMuF"
                          onChange={(value) => setCaptcha(value)}
                        />
                      <button
                        type="submit"
                        className="w-full bg-[#1E4F8A] text-white py-4 rounded-full font-medium hover:bg-[#163A66] transition-colors flex items-center justify-center gap-2"
                        data-testid="contact-form-submit"
                      >
                        <Send className="w-5 h-5" />
                        Send Enquiry
                       
                      </button>
                                               
                      <p className="text-xs text-center text-[#4A5568]">
                        By submitting this form, you agree to  our Terms & Condition and Privac Police. You will be contacted regarding your enquiry. 
                        Your information will be handled in accordance with our privacy practices.
                      </p>
                    </form>
                  </>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-28 bg-white" id="faq" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#3A7BD5] mb-4 block">
              FAQ
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1E4F8A] mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-[#4A5568]">
              Find answers to common questions about our companionship services.
            </p>
          </motion.div>
          <motion.div {...fadeInUp}>
            <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`faq-${index}`}
                  className="bg-[#F7F9FC] border border-[#E2E8F0] rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
                >
                  <AccordionTrigger className="text-left text-[#1A1A1A] font-medium hover:text-[#1E4F8A] hover:no-underline py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#4A5568] pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Service Area Note */}
      <section className="py-12 bg-[#F7F9FC]" data-testid="service-area-note">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <p className="text-[#4A5568]">
              <strong className="text-[#1A1A1A]">Service Area:</strong> AERW Lifestyle provides 
              companionship services across the United Kingdom. Service availability may vary 
              by location and can be confirmed during your consultation.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
